import { useParams } from "react-router-dom";
import { useFetchClientInsuranceFees } from "../../hooks/clients.queries";
import ClientInsuranceRecordsItem from "./ClientInsuranceRecordsItem/ClientInsuranceRecordsItem";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { ProviderStatus } from "../../../../utils/types/session";
import { IClientInsuranceRecords } from "../../types/clients.types";

function isSelfPay(record: IClientInsuranceRecords) {
    return (
        record.name?.toLowerCase() === "self-pay" ||
        record.fees.some((fee) => fee.self_pay)
    );
}

function getActiveSelfPayFee(record: IClientInsuranceRecords) {
    return record.fees.find(
        (fee) => fee.self_pay && fee.fee_status === ProviderStatus.ACTIVE
    );
}

function isActive(record: IClientInsuranceRecords) {
    return isSelfPay(record)
        ? !!getActiveSelfPayFee(record)
        : record.status === ProviderStatus.ACTIVE;
}

function getStartDate(record: IClientInsuranceRecords) {
    const fee = isSelfPay(record)
        ? getActiveSelfPayFee(record)
        : record.fees[0];
    const startDate =
        record.effective_start_date ??
        record.start_date ??
        fee?.fee_effective_start_date ??
        fee?.start_date;

    return startDate?.split("T")[0];
}

export default function ClientInsuranceRecords() {
    const { clientId } = useParams();

    const {
        error,
        isLoading,
        isSuccess,
        data: records,
    } = useFetchClientInsuranceFees(clientId || "");

    const sortedRecords = (records?.data ?? [])
        .map((record, index) => ({ record, index }))
        .sort((a, b) => {
            const isSelfPayA = isSelfPay(a.record);
            const isSelfPayB = isSelfPay(b.record);

            if (
                getStartDate(a.record) !== getStartDate(b.record) ||
                !isActive(a.record) ||
                !isActive(b.record)
            ) {
                return a.index - b.index;
            }

            return Number(isSelfPayB) - Number(isSelfPayA) || a.index - b.index;
        })
        .map(({ record }) => record);

    return (
        <div className="flex flex-col gap-y-[--spacing-items]">
            {isLoading && <Loader />}
            {error ? (
                <ListState
                    isError
                    errorMsg={
                        error.response?.data.message ||
                        "Error fetching client insurance"
                    }
                    context="general"
                    stateHelperText="Cannot display client insurance at this time, please try again later"
                />
            ) : null}
            {isSuccess && records?.data && records.data.length === 0 ? (
                <ListState
                    isError={false}
                    emptyMessage="No client insurance to display"
                    stateHelperText="Add insurance for this client to get started"
                />
            ) : null}
            {isSuccess &&
                sortedRecords.map((record, index) => (
                    <ClientInsuranceRecordsItem
                        key={record.insurance_id}
                        record={record}
                        isFirst={index === 0}
                    />
                ))}
        </div>
    );
}
